import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { sendOrderConfirmationEmail, notifyTeamOfNewOrder } from '../services/emailService';

const AppContext = createContext(null);

const getStoredAuth = () => {
  try { return JSON.parse(localStorage.getItem('jawhara_auth')); }
  catch { return null; }
};

const getStoredSiteContent = () => {
  try { return JSON.parse(localStorage.getItem('jawhara_site_content') || 'null'); }
  catch { return null; }
};

export const AppProvider = ({ children }) => {
  const [products, setProducts]       = useState([]);
  const [orders, setOrders]           = useState([]);
  const [users, setUsers]             = useState([]);
  const [coupons, setCoupons]         = useState([]);
  const [categories, setCategories]   = useState([]);
  const [clients, setClients]         = useState([]);
  const [clientSectors, setClientSectors] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [siteContent, setSiteContent] = useState(getStoredSiteContent);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [auth, setAuth]               = useState(getStoredAuth);

  /* كل صف في قاعدة البيانات = منتج واحد يظهر للمستخدم، فعدد المنتجات في الموقع
     مطابق دائماً لعددها في الداشبورد. المنتج اللي له أكثر من صورة يستخدم حقل
     gallery، والمنتج اللي له موديلات فعلية يستخدم حقل variants — الاتنين
     بيتظبطوا من الداشبورد، مش باستنتاج تلقائي من الاسم. */
  const groupedProducts = products;

  /* ── Cart — persisted per user in localStorage ── */
  const getCartKey  = (a) => `jawhara_cart_${a?.id || 'guest'}`;
  const getStoredCart = () => {
    try { return JSON.parse(localStorage.getItem(getCartKey(getStoredAuth()))) || []; }
    catch { return []; }
  };
  const [cart, setCartState] = useState(getStoredCart);

  const setCart = (updater) =>
    setCartState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem(getCartKey(getStoredAuth()), JSON.stringify(next)); } catch {}
      return next;
    });

  /* ── Fetch all data ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Each call fails independently — one endpoint erroring (e.g. an expired
      // admin auth token on getOrders/getUsers) must not block the rest of the
      // page's public data (siteContent, products, ...) from updating and leave
      // it stuck showing a stale localStorage snapshot.
      const NONE = Symbol('fetch-failed');
      const [prods, ords, usrs, content, coups, cats, clis, clisec, galimgs] = await Promise.all([
        api.getProducts().catch(() => NONE),
        api.getOrders().catch(() => NONE),
        api.getUsers().catch(() => NONE),
        api.getSiteContent().catch(() => NONE),
        api.getCoupons().catch(() => NONE),
        api.getCategories().catch(() => NONE),
        api.getClients().catch(() => NONE),
        api.getClientSectors().catch(() => NONE),
        // Fails soft: on prod this table only exists after migrate.php has been run,
        // and a missing gallery table shouldn't take the whole site down.
        api.getGalleryImages().catch(() => []),
      ]);
      if (prods !== NONE) setProducts(prods);
      if (ords !== NONE) setOrders(ords);
      if (usrs !== NONE) setUsers(usrs);
      if (content !== NONE) {
        setSiteContent(content);
        try { localStorage.setItem('jawhara_site_content', JSON.stringify(content)); } catch {}
      }
      if (coups !== NONE) setCoupons(coups);
      if (cats !== NONE) setCategories(cats);
      if (clis !== NONE) setClients(clis);
      if (clisec !== NONE) setClientSectors(clisec);
      setGalleryImages(galimgs);
    } catch {
      setError('تعذر الاتصال بالخادم. تأكد من تشغيل قاعدة البيانات (npm start).');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Auth ── */
  const login = async (username, password) => {
    const user = await api.login(username, password);
    setAuth(user);
    localStorage.setItem('jawhara_auth', JSON.stringify(user));
    await fetchAll();
    return user;
  };

  const logout = () => {
    api.logout().catch(() => {});
    localStorage.removeItem(getCartKey(auth));
    setAuth(null);
    localStorage.removeItem('jawhara_auth');
    setCart([]);
  };

  /* ── Register new customer ── */
  const registerCustomer = async ({ name, username, password, phone, email }) => {
    const newUser = await api.createUser({
      name, username, password,
      phone: phone || '', email: email || '',
      role: 'customer',
      createdAt: new Date().toISOString(),
    });
    setUsers(prev => [...prev, newUser]);
    return login(username, password);
  };

  /* ── Products ── */
  const addProduct    = async (d)     => { const n = await api.createProduct(d);      setProducts(p => [...p, n]); return n; };
  const updateProduct = async (id, d) => { const u = await api.updateProduct(id, d);  setProducts(p => p.map(x => String(x.id) === String(id) ? u : x)); return u; };
  const deleteProduct = async (id)    => { await api.deleteProduct(id);                setProducts(p => p.filter(x => String(x.id) !== String(id))); };

  /* ── Users ── */
  const addUser    = async (d)     => { const n = await api.createUser(d);      setUsers(u => [...u, n]); return n; };
  const updateUser = async (id, d) => { const u = await api.updateUser(id, d);  setUsers(p => p.map(x => String(x.id) === String(id) ? u : x)); return u; };
  const deleteUser = async (id)    => { await api.deleteUser(id);                setUsers(p => p.filter(x => String(x.id) !== String(id))); };

  /* ── Coupons ── */
  const addCoupon    = async (d)     => { const n = await api.createCoupon(d);      setCoupons(p => [...p, n]); return n; };
  const updateCoupon = async (id, d) => { const u = await api.updateCoupon(id, d);  setCoupons(p => p.map(x => String(x.id) === String(id) ? u : x)); return u; };
  const deleteCoupon = async (id)    => { await api.deleteCoupon(id);                setCoupons(p => p.filter(x => String(x.id) !== String(id))); };

  /* ── Categories ── */
  const addCategory    = async (d)     => { const n = await api.createCategory(d);      setCategories(p => [...p, n]); return n; };
  const updateCategory = async (id, d) => { const u = await api.updateCategory(id, d);  setCategories(p => p.map(x => String(x.id) === String(id) ? u : x)); return u; };
  const deleteCategory = async (id)    => { await api.deleteCategory(id);                setCategories(p => p.filter(x => String(x.id) !== String(id))); };

  /* ── Clients ── */
  const addClient    = async (d)     => { const n = await api.createClient(d);      setClients(p => [...p, n]); return n; };
  const updateClient = async (id, d) => { const u = await api.updateClient(id, d);  setClients(p => p.map(x => String(x.id) === String(id) ? u : x)); return u; };
  const deleteClient = async (id)    => { await api.deleteClient(id);                setClients(p => p.filter(x => String(x.id) !== String(id))); };

  /* ── Client Sectors ── */
  const addClientSector    = async (d)     => { const n = await api.createClientSector(d);      setClientSectors(p => [...p, n]); return n; };
  const updateClientSector = async (id, d) => { const u = await api.updateClientSector(id, d);  setClientSectors(p => p.map(x => String(x.id) === String(id) ? u : x)); return u; };
  const deleteClientSector = async (id)    => { await api.deleteClientSector(id);                setClientSectors(p => p.filter(x => String(x.id) !== String(id))); };

  /* ── Gallery Images ── */
  const addGalleryImage    = async (d)     => { const n = await api.createGalleryImage(d);      setGalleryImages(p => [...p, n]); return n; };
  const updateGalleryImage = async (id, d) => { const u = await api.updateGalleryImage(id, d);  setGalleryImages(p => p.map(x => String(x.id) === String(id) ? u : x)); return u; };
  const deleteGalleryImage = async (id)    => { await api.deleteGalleryImage(id);                setGalleryImages(p => p.filter(x => String(x.id) !== String(id))); };

  /* ── Orders ── */
  const updateOrderStatus = async (id, status) => {
    const ord = orders.find(o => o.id === id);
    if (!ord) return;
    const updated = await api.updateOrder(id, { ...ord, status });
    setOrders(p => p.map(x => String(x.id) === String(id) ? updated : x));
    return updated;
  };

  /* ── Site Content ── */
  const saveSiteContent = async (data) => {
    const updated = await api.updateSiteContent({ id: 1, ...data });
    setSiteContent(updated);
    try { localStorage.setItem('jawhara_site_content', JSON.stringify(updated)); } catch {}
    return updated;
  };

  /* ── Cart ── */
  const addToCart = (product, qty = 1) => {
    const cartKey = product._cartKey || product.id;
    setCart(prev => {
      const ex = prev.find(i => (i._cartKey || i.id) === cartKey);
      return ex
        ? prev.map(i => (i._cartKey || i.id) === cartKey ? { ...i, qty: i.qty + qty } : i)
        : [...prev, { ...product, _cartKey: cartKey, qty }];
    });
  };

  const removeFromCart = (cartKey) => setCart(prev => prev.filter(i => (i._cartKey || i.id) !== cartKey));

  const updateCartQty = (cartKey, qty) =>
    setCart(prev => qty <= 0
      ? prev.filter(i => (i._cartKey || i.id) !== cartKey)
      : prev.map(i => (i._cartKey || i.id) === cartKey ? { ...i, qty } : i)
    );

  const clearCart = () => setCart([]);

  const cartTotal    = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartTotalQty = cart.reduce((s, i) => s + i.qty, 0);

  /* ── Submit Order ── */
  const submitOrder = async (orderData, options = {}) => {
    const ref = 'ORD-' + Date.now();
    const order = {
      ref,
      ...orderData,
      items: cart,
      total: cartTotal.toFixed(3),
      status: 'pending',
      date: new Date().toLocaleDateString('ar-EG'),
    };
    const saved = await api.createOrder(order);
    const safeSaved = { ...saved };
    delete safeSaved.tapPaymentToken;
    setOrders(prev => [...prev, safeSaved]);

    /* ── Send confirmation email to customer ── */
    try { await sendOrderConfirmationEmail(safeSaved); } catch (e) { console.warn('Email not sent:', e); }

    /* ── Notify Al-Jawhara team of the new order ── */
    try { await notifyTeamOfNewOrder(safeSaved); } catch (e) { console.warn('Team notification not sent:', e); }


    if (!options.preserveCart) clearCart();
    return saved;
  };

  return (
    <AppContext.Provider value={{
      products, groupedProducts, orders, users, coupons, categories, clients, clientSectors, galleryImages, siteContent, loading, error, auth,
      login, logout, registerCustomer,
      addProduct, updateProduct, deleteProduct,
      addUser, updateUser, deleteUser,
      addCoupon, updateCoupon, deleteCoupon,
      addCategory, updateCategory, deleteCategory,
      addClient, updateClient, deleteClient,
      addClientSector, updateClientSector, deleteClientSector,
      addGalleryImage, updateGalleryImage, deleteGalleryImage,
      updateOrderStatus,
      saveSiteContent,
      cart, addToCart, removeFromCart, updateCartQty, clearCart,
      cartTotal, cartTotalQty,
      submitOrder,
      refresh: fetchAll,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
};
